import { Bin, Method } from 'ag-sockets/dist/browser';
import { getActionLog, getTimestamp, logAction } from '../common/actionLog';
import { findById, hasFlag, includes, invalidEnum, removeItem } from '../common/baseUtils';
import { BinConnId, BinCoord, BinLayerId, BinLocalId, BinPressure, BinSequenceUser } from '../common/bin';
import { readMovesEntry } from '../common/binary';
import { getPixelContext, replaceImageDataRect } from '../common/canvasUtils';
import { getEntityPassword, isInAngularZone, navigateToDrawing, popOpenedBy, setEntityPassword } from '../common/clientUtils';
import { clearBufferedEncoder, decodeCoord, decodePressure, nextToolArrayDecoder, startDecoder } from '../common/compressor';
import {
  CHAT_MESSAGE_HISTORY_LIMIT, CLIENT_LIMIT_ERROR, DRAWING_DELETED, DRAWING_NOT_FOUND,
  DRAWING_NO_ACCESS, isUserLimitError, MAINTENANCE_ERROR, MINUTE, PASSWORD_ERROR, SEQUENCE_FRAME_LIMIT,
  SESSION_LIMIT_ERROR, SHOW_CURSORS, SIGN_IN_TO_VIEW_ERROR, WEEK, PRESENTATION_VIEWER_FORCED_INVITE_TIME, PRESENTATION_HOST_DISCONNECTED_TIME, PRESENTATION_TAKE_OVER_TIME
} from '../common/constants';
import { isFromSocial } from '../common/data';
import { createSequenceDrawing, getLayer, getMainDrawingId, setLayerOwner, setOwnedLayer } from '../common/drawing';
import { History, redosLog, undosLog } from '../common/history';
import {
  ApplyFunc, ChatType, ClientAction, ClientDrawingData, DisownFlags, DrawingDataUpdated,
  DrawingLoadFailure, IChatMessage, IClient, InitParams, IServerTool, IToolData, IUserSelection, JoinPresentation, LastDisconnectOnClient,
  Layer, LeavePresentation, LoadingResult, PresentationActions, PresentationModeInvite, PresentationModeState, PromptHistoryItem, QuickAction, RecentDrawingData, SequenceAction, SequenceUserData, SocketWithDrawings,
  ToolId, ToolSource, UpdateOwnerName, User, UserData, Viewport, ViewportState, VoiceChatAction
} from '../common/interfaces';
import { loadingEnd, loadingStart, reportLoadingStats, startLoadingStats } from '../common/loading';
import { clearMask, isMaskEmpty } from '../common/mask';
import { delay } from '../common/promiseUtils';
import { createRect, rectToString } from '../common/rect';
import { decompressImageDataRLE } from '../common/rle';
import { addBrushShape, addShape, brushShapes, brushShapesMap, createShapePath, shapeShapes } from '../common/shapes';
import { isSelectionTool, toolIdToString } from '../common/toolIdUtils';
import { createFakeModel, createTool, getToolFullName } from '../common/tools';
import { IData, SelectionHelperAction } from '../common/tools/selectionHelperTool';
import { applyTool, finishTransform, setLastToolSource, setupActiveTool, verifyAfterRect, verifySelection } from '../common/toolUtils';
import { redrawSequenceThumbCanvas } from '../common/update';
import {
  clearUsers, createAndInitUser, createUser, lastXYToCursorXY, loadUserAvatarImage,
  releaseToolRenderingContext,
  releaseUser, resetUser, setActiveLayer, setUserState, userOwnsLayer
} from '../common/user';
import { hasPermission } from '../common/userRole';
import { fullName } from '../common/userUtils';
import { callNowOrAfterSwitchedToTab, findByLocalId, reorder, setDocumentTitle } from '../common/utils';
import { sendGAEvent } from '../common/utilsAnalytics';
import { copyViewport, createViewport, fitViewportOnScreen, matchFromOtherViewport } from '../common/viewport';
import { wasmFailed } from '../common/wasmUtils';
import { cancelTool, cancelToolInternal, Editor, lockEditor, setDrawing, unlockEditor } from './editor';
import { redraw, redrawDrawing } from './editorUtils';
import { dismissChatHelp, showChatHelp } from './help';
import { addAndSelectLayer, disownAllLayers, disownLayer, ownLayer, selectLayer } from './layerActions';
import { isActiveClientActions, Model } from './model';
import { userChanged, sendLayerSnapshot, createClientBrowserInfo } from './real-model';
import { loadSettings, resetRemoteSettings } from './settingsService';
import { storageGetBoolean, storageGetJson, storageRemoveItem, storageSetItem } from './storage';

export const toHistory: (keyof ClientActions)[] = [];
export const toWait: (keyof ClientActions)[] = [];

// all decorator functions have to be separate and exported to work with ng-packagr

export function createApplyFunc(method: any) {
  return function (this: ClientActions, ...args: any[]) {
    return this.apply(() => method.apply(this, args));
  };
}

export function applyFunc(_target: Object, _name: string, descriptor: TypedPropertyDescriptor<any>) {
  const method = descriptor.value!;
  descriptor.value = createApplyFunc(method);
}

export function toHistoryFunc(_target: Object, name: string) {
  toHistory.push(name as keyof ClientActions);
}

export function waitFunc(_target: Object, name: string) {
  toWait.push(name as keyof ClientActions);
}

export function Apply() { return applyFunc; }
export function ToHistory() { return toHistoryFunc; }
export function Wait() { return waitFunc; }

let shownOverloaded = false;
let shownNotSignedIn = false;
let disconnectedCodeReason: LastDisconnectOnClient | undefined = undefined;
let disconnectedState: {
  id: string;
  activeLayerId: number;
  visibleLocally: { id: number; visibleLocally: boolean | undefined; }[];
  // TODO: selection to restore
} | undefined = undefined;

const failedErrors: { [key: string]: DrawingLoadFailure; } = {
  [DRAWING_NOT_FOUND]: DrawingLoadFailure.NotFound,
  [SESSION_LIMIT_ERROR]: DrawingLoadFailure.SessionLimit,
  [DRAWING_NO_ACCESS]: DrawingLoadFailure.NoAccess,
  [SEQUENCE_FRAME_LIMIT]: DrawingLoadFailure.FrameLimit,
};

export class ClientActions implements IClient {
  lastJobId = '-1';
  constructor(public socket: SocketWithDrawings, private model: Model, public apply: ApplyFunc) {
  }
  private get isActive() {
    return isActiveClientActions(this.model, this);
  }
  private activeConn(connId: number, func: string) {
    if (DEVELOPMENT && TESTS && connId !== this.model.connId) throw new Error(`Invalid connId: ${connId} !== ${this.model.connId} ${this.model.user.name} (func: ${func})`);
    if (DEVELOPMENT && connId !== this.model.connId) console.warn(`Invalid connId: ${connId} !== ${this.model.connId} (func: ${func})`);
    if (!this.isActive) logAction(`ClientActions not active (func: ${func})`);
    else if (connId !== this.model.connId) logAction(`invalid connId: ${connId} !== ${this.model.connId} (func: ${func})`);
    return this.isActive && connId === this.model.connId;
  }
  // TODO: remove @Wait from here, instead make sure we flush all queues and loading process
  @Wait()
  connected() {
    if (!this.isActive) return;

    logAction('connected');
    if (this.model.error === MAINTENANCE_ERROR) this.model.error = undefined;
    this.model.loaded = 0;
    this.model.loadingFailed = false;
    this.model.failed = false;
    this.model.failedDrawingLoad = DrawingLoadFailure.None;
    this.model.toolCounter = 1;
    this.model.pressureApiSent = false;
    this.model.rendererApiSent = false;
    this.model.clearQueuedSends();
    this.model.clearPendingFiles();
  }
  // TODO: remove @Wait from here, instead make sure we flush all queues and loading process
  @Wait() @Apply()
  disconnected(code: number, reason: string) {
    const model = this.model;

    if (this.isActive) {
      logAction('disconnected');

      // unexpected disconnect
      if (model.editor) {
        if (model.editor.drawing.id) {
          const drawing = model.editor.drawing;
          disconnectedCodeReason = { code, reason };
          disconnectedState = {
            id: drawing.id,
            activeLayerId: model.user.activeLayerId,
            visibleLocally: drawing.layers
              .filter(l => l.visibleLocally !== undefined)
              .map(({ id, visibleLocally }) => ({ id, visibleLocally })),
          };

          // discard thumb, so we don't send it when we no longer have open drawing on the socket
          drawing.thumbUpdate = undefined;
          model.editor.renderer?.discardThumb();
        }

        model.editor.activeTool?.cancelBeganTool?.();
        cancelTool(model.editor, 'disconnect'); // cancelTool needs to happen before isConnected is set to false
      }

      model.cancelLoading();
      // this.model.isConnected = false;
      this.socket.initialized = false; // this needs to happen before connectedStateChanged.next ?
      model.passwordNeeded = false;
      model.connectedStateChanged.next(false);
      model.clearQueuedSends();
      model.clearPendingFiles();
    }

    this.socket.initialized = false;
    const sequence = model.editor?.drawing.sequence;

    if (sequence) {
      // remove users from sequence drawings
      for (const id of this.socket.drawings) {
        const drawing = findById(sequence, id);
        if (drawing) drawing.users.length = 0;
      }
    }
  }
  invalidVersion() {
    location.reload();
  }
  @Wait() @Apply() @Method({ binary: [Bin.Str, Bin.Obj, Bin.Str, Bin.Obj] })
  init(sessionId: string, user: UserData, openingId: string | undefined, params: InitParams) {
    storageSetItem('sessionId', sessionId);

    const connection = this.model.connectionService;
    for (const { socket } of connection.sockets) {
      socket.options.requestParams.sessionId = sessionId;
    }

    this.socket.initialized = true;

    if (this.socket.drawings && connection.sequenceIds.length > 0) {
      const ids = this.socket.drawings.filter(id => includes(connection.sequenceIds, id));
      if (ids.length) this.socket.socket.server.trackDrawings(ids, ids.map(getEntityPassword));
    }

    if (!this.isActive) return;

    const { drawingId, connId } = connection;

    if (!openingId && drawingId) {
      this.model.server.openDrawing(connId, drawingId, getEntityPassword(drawingId), popOpenedBy(drawingId));
    } else if (openingId && openingId !== drawingId) {
      this.model.server.closeDrawing();
    }

    logAction('init');
    // this.model.isConnected = true;
    this.model.loaded = 0;
    this.model.loadingFailed = false;
    this.model.failed = false;
    clearBufferedEncoder(this.model.encoder);
    clearAllUsers(this.model);
    this.model.user = createAndInitUser(user.uniqId, 0, user, this.model.editor);
    loadUserAvatarImage(this.model.user);
    userChanged(this.model);

    // report last disconnect reason
    if (disconnectedCodeReason) {
      this.model.server.quickAction(QuickAction.DisconnectReason, disconnectedCodeReason);
      disconnectedCodeReason = undefined;
    }

    // report stats
    const wnd = typeof window !== 'undefined' ? window : undefined;
    let frame: string | undefined = undefined;
    try {
      if (wnd && parent !== wnd) frame = document.referrer;
    } catch { }
    this.model.server.quickAction(QuickAction.CheckUrl, location.href);
    if (frame) this.model.server.quickAction(QuickAction.FrameUrl, frame);

    // TODO: move this from here, this may be called before wasm fails
    if (wasmFailed) this.model.server.quickAction(QuickAction.WasmFailed, wasmFailed);

    this.model.server.quickAction(QuickAction.BrowserInfo, createClientBrowserInfo());
    this.model.server.quickAction(QuickAction.BrushShapesLoaded, brushShapes.map(s => s.id));
    this.model.server.quickAction(QuickAction.ShapeShapesLoaded, shapeShapes.map(s => s.id));

    // dismiss help messages for week old accounts
    if (user.created && (Date.now() - (new Date(user.created)).getTime()) > WEEK) {
      dismissChatHelp();
    }

    // show sign-in encouragement for old sessions
    if (!IS_HOSTED && !IS_PORTAL && user.anonymous && !/^anonymous$/i.test(user.name) && params.showNotSignedIn && !shownNotSignedIn) {
      this.model.onUserError.next({ message: `You're not signed in, create an account to preserve your settings and get access to admin features.` });
    }
    shownNotSignedIn = true; // ouside `if`, so we don't show not-signed-in notice when user signs out

    this.model.newFeature = params.newFeature ?? '';
    this.model.connectedStateChanged.next(true);
  }
  @Apply() @Method({ /*binary: [Bin.Str]*/ })
  settings(settings: string) {
    if (!this.isActive) return;

    loadSettings(this.model, settings);
    this.model.settingsInitialized = true;
  }
  @Apply() @Method({ /*binary: [BinConnId, Bin.Str, Bin.Str, Bin.U8]*/ })
  chat(connId: number, uniqId: string, message: string, type: ChatType) {
    if (connId && !this.activeConn(connId, 'chat')) return; // skip for 0 for sequence chat
    if (invalidModel(this.model)) return;

    chatMessage(this.model, uniqId, message, type);
  }
  @Apply() @Method({ binary: [Bin.Str, Bin.Str] })
  announcement(message: string, type: string) {
    if (!this.isActive) return;

    this.model.announcement = message ? { message, type } : undefined;
  }
  // Users
  @Wait() @ToHistory() @Apply() @Method({ binary: [BinConnId, Bin.Obj] })
  addUser(connId: number, data: UserData) {
    if (!this.activeConn(connId, 'addUser')) return;
    if (invalidModel(this.model)) return;

    const model = this.model;
    const editor = getEditor(model);
    let user = findByLocalId(model.tempUsers, data.localId);
    logAction(`[remote] addUser ${user ? '(promote temp) ' : ''}(clientId: ${data.localId})`);

    if (user) {
      user.uniqId = data.uniqId;
      setUserState(user, data);
      removeItem(model.tempUsers, user);
      // resetUser(user, editor); // this was resetting history, breaking removeAutoGeneratedLayer in some cases
    } else {
      user = createAndInitUser(data.uniqId, data.localId, data, editor);
    }

    loadUserAvatarImage(user);

    const drawing = editor.drawing;
    model.users.push(user);
    user.ownedLayers.forEach(layerId => setOwnedLayer(drawing, layerId, user!));
    setActiveLayer(user, getLayer(drawing, user.activeLayerId));

    if (model.users.length && showChatHelp()) {
      setTimeout(() => {
        if (showChatHelp()) {
          model.showChatPrompt = true;
        }
      }, 30 * 1000);
    }
  }
  @Wait() @ToHistory() @Apply() @Method({ binary: [BinConnId, Bin.Obj, Bin.Str] })
  updateUser(connId: number, data: UserData) {
    if (!this.activeConn(connId, 'updateUser')) return;
    if (this.model.fatalError) return;

    logAction(`[remote] updateUser (clientId: ${data.localId})`);
    const model = this.model;
    const user = model.getUserByUniqId(data.uniqId, false);

    // TEMP: testing
    if (!user) {
      throw new Error(`[updateUser] Missing user\n` +
        `    data: ${JSON.stringify(data)}\n` +
        `    loading: ${model.loaded}, loadingPromise: ${!!model.loadingPromise}\n` +
        `    user ${JSON.stringify({ uniqId: model.user.uniqId, localId: model.user.localId, name: model.user.name })}\n` +
        `    users ${JSON.stringify(model.users.map(u => ({ uniqId: u.uniqId, localId: u.localId, name: u.name })))}`);
    }

    if (user === model.user) {
      // upgraded to pro, checking if email is the same in case user just signed-in to a different account
      if (IS_PORTAL && !IS_HOSTED && !user.pro && data.pro && !user.anonymous && user.email === data.email) {
        // show upgrade notification
        if (data.subscriptionStatus?.status === 'trialing') {
          // TODO: maybe show different notification when not trialing ?
          callNowOrAfterSwitchedToTab(() => model.onUserError.next({ message: `UPGRADED`, type: 'success' }));
        }

        if (model.failedDrawingLoad === DrawingLoadFailure.SessionLimit) {
          model.reconnect(); // reconnect because session limit changed for the user
        }
      }

      // user signed-in or signed-out
      if (user.anonymous !== data.anonymous) {
        resetRemoteSettings(model);
      }

      // in case voice chat permission changed
      model.voiceChat.applySettings();
      model.voiceChat.updateUserMutes();

      if (model.failedDrawingLoad && model.openDrawingId) {
        location.reload();
      }
    }

    // update voice chat settings
    if (user.name !== data.name) {
      model.voiceChat.updateUserName(user.name, data.name);
    }

    const oldFullName = fullName(user);
    const owned = user.ownedLayers.slice(0);

    setUserState(user, data);
    loadUserAvatarImage(user);

    // update layer ownership
    if (model.editor) {
      const drawing = model.editor.drawing;

      owned
        .filter(layerId => !userOwnsLayer(user, layerId))
        .forEach(layerId => setOwnedLayer(drawing, layerId, undefined));

      user.ownedLayers
        .forEach(layerId => setOwnedLayer(drawing, layerId, user));

      if (user !== model.user || !model.editor.drawingInProgress) {
        setActiveLayer(user, getLayer(drawing, user.activeLayerId));
      }

      if (user === model.user && !user.activeLayer) {
        // select first owned layer
        const firstOwnedLayer = drawing.layers.find(l => l.owner === model.user);
        if (firstOwnedLayer) model.editor.selectLayer(firstOwnedLayer);
      }

      // update layer owner names
      for (const layer of drawing.layers) {
        if (layer.layerOwner?.name === oldFullName) {
          layer.layerOwner.name = fullName(user);
          layer.layerOwner.color = user.color;
        }
      }
    }

    if (user === this.model.user) {
      userChanged(this.model);
    }
  }
  private removedUsers: User[] = [];
  @Wait() @ToHistory() @Apply() @Method({ binary: [BinConnId, BinLocalId] })
  removeUser(connId: number, localId: number) {
    if (!this.activeConn(connId, 'removeUser')) return;

    logAction(`[remote] removeUser (clientId: ${localId})`);
    const user = findByLocalId(this.model.users, localId);

    if (user) {
      if (this.model.sendingHistory) {
        this.removedUsers.push(user);
      } else {
        this.removeUserInternal(user);
      }
    }
  }
  private removeUserInternal(user: User) {
    const editor = this.model.editor;

    if (editor) {
      finishTransform(editor, user, 'removeUserInternal');

      for (const id of user.ownedLayers) {
        const layer = getLayer(editor.drawing, id);
        if (layer) layer.owner = undefined;
      }
    }

    removeItem(this.model.users, user);
    resetUser(user, editor);

    if (editor) redraw(editor);
  }
  @Wait() @ToHistory() @Method({ binary: [BinConnId, BinLocalId, [Bin.U8]] })
  userSelection(connId: number, localId: number, data: number[]) {
    if (!this.activeConn(connId, 'userSelection')) return;
    if (invalidModel(this.model)) return;

    logAction(`[remote] userSelection (clientId: ${localId})`);
    void execTool(this.model, localId, 0, <IData>{
      id: ToolId.SelectionHelper,
      action: SelectionHelperAction.InitSelection,
      selectionData: data,
    }, undefined, undefined);

    this.model.toolPromise?.catch(e => {
      this.model.fatalError = 'Failed to draw tool';
      this.model.reportError('Failed on toolPromise (selection)', e);
    });
  }
  @Apply() @Method({ binary: [Bin.Obj] })
  updateRecentDrawing(recentDrawing: RecentDrawingData) {
    if (!this.isActive) return;

    const user = this.model.user;
    const drawing = user.recentDrawings && findById(user.recentDrawings, recentDrawing.id);

    if (drawing) {
      drawing.name = recentDrawing.name;
    }
  }
  // Drawing
  @Wait() @Apply() @Method({
    binary: [
      BinConnId, // connId
      Bin.Obj, // user
      Bin.Obj, // state
      [Bin.U8, Bin.U16, Bin.F64, Bin.F64, Bin.F64, Bin.F64, Bin.Bool, Bin.Obj, Bin.U8Array, Bin.Bool, [BinLayerId], Bin.Bool, [Bin.Obj]], // tools
      [Bin.Obj], // updates
      [Bin.U8, [Bin.U8]] // userSelections
    ]
  })
  async drawingOpen(
    connId: number, userData: UserData, state: ClientDrawingData, tools: IServerTool[], updates: DrawingDataUpdated[],
    userSelections: IUserSelection[]
  ) {
    if (!this.activeConn(connId, 'drawingOpen')) return;

    logAction(`[remote] drawingOpen (id: ${state.id}, conn: ${connId}, width: ${state.width}, height: ${state.height}, layers: [${state.layers.map(l => l.id).join(', ')}])`);

    const model = this.model;
    const editor = getEditor(model);
    let error: string | undefined = undefined;
    let cancelled = false;

    if (model.openDrawingId !== state.id) {
      logAction(`model.openDrawingId !== state.id (openDrawingId: ${model.openDrawingId}, id: ${state.id})`);
      model.loadingFailed = true;
      return;
    }

    startLoadingStats();
    loadingStart('drawingOpen()');

    if (IS_PORTAL) {
      void this.model.modals.closeOverloaded();
    }

    try {
      model.passwordNeeded = false;
      model.sendingHistory = true;
      model.drawingHistory = [];
      model.waitingActions = [];
      model.selectLayerOnOwn = 0;

      this.removedUsers = [];
      this.callFinishedLoadingDrawing = false;

      const mainDrawingId = getMainDrawingId(state);

      if (IS_PORTAL && mainDrawingId !== this.model.voiceChat.drawingId) {
        this.model.messages = [];
      }

      if (mainDrawingId !== this.model.voiceChat.drawingId) {
        model.voiceChat.reset();
        model.voiceChat.drawingId = mainDrawingId;
      }

      if (DEVELOPMENT && /tool-promise/.test(model.user.name)) {
        model.toolPromise = delay(5000).then(() => model.toolPromise = undefined);
      }

      if (model.toolPromise) {
        logAction(`waiting for toolPromise`);
        await model.toolPromise.catch(e => DEVELOPMENT && console.error(e));
        if (model.connId !== connId) throw new Error('Cancelled');
      }

      cancelTool(editor, 'drawingOpen'); // cancel can leave selection non-empty, needs to be before reset
      editor.movingView = false;

      resetUser(model.user, editor);

      if (!isMaskEmpty(model.user.selection)) logAction(`error: user selection not empty after resetUser`); // TEMP

      // set to empty owned layer list so later it will correctly update owned layers
      setUserState(model.user, { ...userData, ownedLayers: [] });

      clearUsers(model.users, editor);
      clearUsers(model.tempUsers, editor);
      clearBufferedEncoder(model.encoder);

      loadingStart('init renderer');
      setDrawing(editor, state); // initializes renderer
      loadingEnd();

      resetUser(model.user, editor); // reset again, because setDrawing can set selection back by cancelling tools

      lockEditor(editor, 'drawingOpen');

      if (!isMaskEmpty(model.user.selection)) logAction(`error: user selection not empty after lockEditor`); // TEMP

      setEntityPassword(state.id, state.password);

      model.loaded = 0;
      model.loadingFailed = false;
      model.failed = false;
      model.failedDrawingLoad = DrawingLoadFailure.None;
      model.toolCounter = 1;
      model.pendingLayerOwns.clear();
      model.clearQueuedSends();

      if (!isMaskEmpty(model.user.selection)) logAction(`error: user selection not empty after clearQueuedSends`); // TEMP

      model.sentTools = 0;
      model.confirmedTools = 0;
      model.dismissError();
      model.user.uniqId = userData.uniqId;
      model.user.localId = userData.localId;
      model.user.history = new History(model.user, editor);
      model.cancelLoading();

      if (!isMaskEmpty(model.user.selection)) logAction(`error: user selection not empty after cancelLoading`); // TEMP

      // multiply by 0.9 to make sure we don't get to 100% yet
      model.loadingPromise = editor.renderer.loadLayerImages(editor.drawing, model, p => model.loaded = p * 0.9);

      model.connectionService.sequenceIds = state.sequence?.map(x => x.id) ?? [];
      model.connectionService.update();

      logAction(`loading layers`);

      if (TESTS && model.testPromise) await model.testPromise; // for testing

      if (!isMaskEmpty(model.user.selection)) logAction(`error: user selection not empty before loadingPromise`); // TEMP

      return model.loadingPromise.then(async ({ loadersUsed }) => {
        if (model.connId !== connId) throw new Error('Cancelled');
        if (DEVELOPMENT && /delay-loading/.test(model.user.name)) await delay(5000);

        if (!isMaskEmpty(model.user.selection)) logAction(`error: user selection not empty after loadingPromise`);

        logAction(`loaded layers (id: ${editor.drawing.id}, layers: [${editor.drawing.layers.map(l => l.id).join(', ')}])`);

        model.server.quickAction(QuickAction.LoadersUsed, loadersUsed);
        model.loadedDrawing(connId, LoadingResult.LoadedLayers);
        model.insideCommitLoop = true;

        logAction(`loading userSelections`);
        loadingStart('userSelections');
        for (const [localId, selectionData] of userSelections) {
          if (selectionData) {
            void execTool(model, localId, 0, <IData>{ id: ToolId.SelectionHelper, action: SelectionHelperAction.InitSelection, selectionData }, undefined, undefined);
            if (model.toolPromise) {
              logAction('await toolPromise');
              await model.toolPromise;
              if (model.connId !== connId) throw new Error('Cancelled');
            }
            const user = model.getUserOrTemp(localId);
            const r = user ? user.selection.bounds : createRect(0, 0, 0, 0);
            logAction(`  selection ${localId} (${r.x} ${r.y} ${r.w} ${r.h})`);
          }
        }
        loadingEnd();

        if (!isMaskEmpty(model.user.selection)) logAction(`error: user selection not empty after userSelections`);

        logAction(`loading tools`);
        loadingStart('tools');
        for (const [localId, layerId, x, y, scale, rotation, flipped, tool, data, finishAfter, disown, hasUpdates, updates] of tools) {
          logAction(`  tool ${toolIdToString(tool.id)} (clientId: ${localId}, layerId: ${layerId}, t: ${tool.t}, finishAfter: ${finishAfter}, disown: [${disown?.join(', ')}])`);

          if (hasUpdates) {
            model.toolPromise = execToolWithUpdates(model, localId, layerId, tool, updates);
          } else {
            void execTool(model, localId, layerId, tool, { x, y, scale, rotation, flipped }, data, finishAfter);
          }

          if (model.toolPromise) {
            logAction('await toolPromise');
            await model.toolPromise;
            model.toolPromise = undefined;
            if (model.connId !== connId) throw new Error('Cancelled');
          }

          for (const id of disown) {
            const user = model.getUserOrTemp(localId);
            if (user) {
              if (user.surface.layer?.id === id) finishTransform(editor, user, 'loading tools'); // TODO: write tests
              user.history.clearLayer(id);
            }
          }
        }
        // TODO: write tests (this might not be possible to break because it will be followed by either disown or 15+ actions)
        for (const user of model.users) {
          finishTransform(editor, user, 'after loading tools');
          user.history.clear();
        }
        loadingEnd();

        if (!isMaskEmpty(model.user.selection)) logAction(`error: user selection not empty after loading tools`);

        logAction(`loading updates`);
        loadingStart('updates');
        for (const update of updates) {
          Object.assign(editor.drawing, update);
        }
        loadingEnd();

        logAction(`loading finishTransform`);
        loadingStart('finishTransform');
        for (const user of model.tempUsers) {
          finishTransform(editor, user, 'loading finishTransform');
        }
        loadingEnd();

        if (!isMaskEmpty(model.user.selection)) logAction(`error: user selection not empty after loading finishTransform`);

        unlockEditor(editor);

        logAction('loading history');
        loadingStart('history');
        while (model.drawingHistory.length) {
          loadingStart('action');
          model.drawingHistory.shift()!();
          if (model.toolPromise) {
            logAction('await toolPromise');
            await model.toolPromise;
            if (model.connId !== connId) throw new Error('Cancelled');
          }
          loadingEnd();
        }
        loadingEnd();

        if (!isMaskEmpty(model.user.selection)) logAction(`error: user selection not empty after loading history`);

        logAction('loading rest');
        loadingStart('rest');

        // setup layer owners
        const drawing = editor.drawing;

        for (const layer of drawing.layers) {
          layer.owner = undefined;
        }

        for (const user of model.users) {
          for (const id of user.ownedLayers) {
            const layer = getLayer(drawing, id);
            if (layer) setLayerOwner(layer, user);
          }

          setActiveLayer(user, getLayer(drawing, user.activeLayerId));
        }

        for (const id of model.user.ownedLayers) {
          const layer = getLayer(drawing, id);
          if (layer) setLayerOwner(layer, model.user);
        }

        editor.boundsChanged = true;
        setDocumentTitle(drawing.name);
        loadingEnd();

        const d = drawing;
        logAction(`loaded (drawingId: ${d.id}, width: ${d.width}, height: ${d.height}, layers: ${d.layers.map(l => l.id).join(', ')})`);

        logAction('commiting waiting actions');
        loadingStart('commiting waiting actions');
        await commitWaitingActions(model);
        model.insideCommitLoop = false;
        if (model.connId !== connId) throw new Error('Cancelled');
        loadingEnd();

        model.sendingHistory = false;

        logAction(`remove removed users (${this.removedUsers.map(u => u.localId).join(', ')})`);
        for (const user of this.removedUsers) {
          this.removeUserInternal(user);
        }

        logAction(`on loaded`);
        model.onLoaded();

        this.updateUser(connId, userData);
        logAction(`updated user`);

        // TEMP: testing, this should never happen
        if (!isMaskEmpty(model.user.selection)) {
          logAction(`error: user selection not empty after load`);
          model.reportError(`User selection not empty after load`, undefined);
          clearMask(model.user.selection);
        }

        // TEMP: testing, this should never happen (still happening as of June 2023)
        if (model.user.history.canUndo() || model.user.history.canRedo()) {
          logAction(`error: user undos/redos not empty after load`);
          model.reportError(`User undos/redos not empty after load`, undefined, { undos: undosLog(model.user.history), redos: redosLog(model.user.history) });
          model.user.history.clear();
        }

        if (isFromSocial === state.id) {
          model.server.quickAction(QuickAction.MarkDrawingAsPublic);
        }

        model.loaded = 1;
        model.loadedDrawing(connId, LoadingResult.Finished);

        if (this.callFinishedLoadingDrawing) {
          this.finishedLoadingDrawing();
        } else {
          this.callFinishedLoadingDrawing = true;
        }
      }).catch((e: Error) => {
        logAction(`loading failed(1) (${e.message})`);
        DEVELOPMENT && !TESTS && console.warn(e.message);
        error = e.message;

        if (e.name === 'AbortError' || /^Cancelled/i.test(e.message)) {
          cancelled = true;
        } else {
          if (!/Error loading image/.test(e.message)) {
            model.reportError('clientDrawing(1)', e);
          }

          model.showError(`Failed to load drawing (${e.message})`, false, true);
        }

        model.loadedDrawing(connId, LoadingResult.Failed);
        model.toolStarted = false;
        model.loadingFailed = true;
        lockEditor(editor, 'loading failed(1)');
      }).finally(() => {
        logAction('loading finally');
        model.loadingPromise = undefined;
        loadingEnd();

        if (!cancelled) {
          try {
            reportLoadingStats(model, state, editor.renderer?.name ?? 'none', error);
          } catch (e) {
            DEVELOPMENT && console.error(e);
          }
        }
      });
    } catch (e) {
      logAction(`loading failed(2) (${e.message})`);
      model.reportError('clientDrawing(2)', e);
      model.fatalError = `Failed to load drawing (${e.message})`;
      model.loadedDrawing(connId, LoadingResult.Failed); // TODO: might be re-opening the same drawing, add opening id ?
      model.toolStarted = false;
      model.loadingFailed = true;
      lockEditor(editor, 'loading failed(2)');
      loadingEnd();
      return Promise.resolve();
    }
  }
  @Wait() @Method({ binary: [BinConnId] })
  drawingTools(connId: number) {
    if (!this.activeConn(connId, 'drawingTools')) return;

    // logAction(`[remote] drawingTools`);
    // TODO: move tools from drawing() method here
  }
  @Wait() @Method({ binary: [BinConnId] })
  drawingLoaded(connId: number) {
    if (!this.activeConn(connId, 'drawingLoaded')) return;
    if (invalidModel(this.model)) return;

    if (DEVELOPMENT && !TESTS && isInAngularZone() && !this.model.insideCommitLoop) {
      console.error('Socket message inside angular zone');
    }

    logAction(`[remote] drawingLoaded (conn: ${connId})`);
    // this.model.sendingHistory = false;

    if (this.callFinishedLoadingDrawing) {
      this.apply(() => this.finishedLoadingDrawing());
    } else {
      this.callFinishedLoadingDrawing = true;
    }
  }
  private callFinishedLoadingDrawing = false;
  private isFirstDrawingLoading = true;
  private finishedLoadingDrawing() {
    logAction(`[remote] finishedLoadingDrawing`);

    const model = this.model;
    const editor = model.editor!;
    const drawing = editor.drawing;

    clearUsers(model.tempUsers, editor);

    model.finishedLoadingDrawing?.next(drawing);

    // select first owned layer, have to do it here when editor is no longer locked
    const firstOwnedLayer = drawing.layers.find(l => l.owner === model.user);
    if (firstOwnedLayer) editor.selectLayer(firstOwnedLayer);

    // we need to pick layer or create a new one on client-side because server doesn't have accurate layer list
    // this needs to be called after drawing is fully loaded so we have complete list of layers
    if (!TESTS && !firstOwnedLayer && hasPermission(drawing, model.user, 'ownLayer')) {
      // don't automatically pick layers that had previous owners that left
      const freeLayer = drawing.layers.find(l => !l.owner && (!l.layerOwner || l.layerOwner?.name === fullName(model.user)));

      if (drawing.justImported) {
        Promise.all(drawing.layers.filter(l => !l.owner).map(l => ownLayer(editor, l)))
          .catch(e => DEVELOPMENT && console.error(e));
      } else if (freeLayer) {
        ownLayer(editor, freeLayer)
          .catch(e => DEVELOPMENT && console.error(e));
      } else {
        setLastToolSource(ToolSource.Auto);
        addAndSelectLayer(editor, true)
          .then(() => model.user.history.clear()) // prevent undoing auto-generated layer
          .catch(e => DEVELOPMENT && console.error(e));
      }
    }

    // restore state from before disconnect
    if (!TESTS && disconnectedState) {
      if (disconnectedState.id === drawing.id) {
        const layer = getLayer(drawing, disconnectedState.activeLayerId);
        if (layer && layer.owner === model.user) selectLayer(editor, layer);

        for (const { id, visibleLocally } of disconnectedState.visibleLocally) {
          const layer = getLayer(drawing, id);
          if (layer) layer.visibleLocally = visibleLocally;
        }
      }

      disconnectedState = undefined;
    }

    // hide all cursors, because they got placed by executing tools from history
    for (const user of model.users) {
      user.cursorX = 1e9;
      user.cursorY = 1e9;
    }

    if (!SERVER && !TESTS && storageGetBoolean('isPresentationModeHost') && document.hasFocus() && this.isFirstDrawingLoading) {
      model.activePresentation && model.continuePresentationMode();
    }

    this.isFirstDrawingLoading = false;
  }
  @Wait() @Apply() @Method({ binary: [BinConnId, BinLocalId, Bin.Obj] })
  updateDrawing(connId: number, localId: number, data: DrawingDataUpdated) {
    if (!this.activeConn(connId, 'updateDrawing')) return;
    if (invalidModel(this.model)) return;

    logAction(`[remote] updateDrawing (clientId: ${localId})`);
    const editor = getEditor(this.model);
    const drawing = editor.drawing;

    if ('sequence' in data) {
      const seq = drawing.sequence;
      const { _id, id, name } = drawing;
      const current = findById(seq, id) ?? { _id, id, name, users: [] };
      drawing.sequence = data.sequence?.map(data =>
        Object.assign(findById(seq, data.id) ?? createSequenceDrawing(data), data)) ?? [current];
      this.model.connectionService.sequenceIds = drawing.sequence.map(s => s.id) ?? [];
      this.model.connectionService.update();
      delete data.sequence;
    }

    if ('password' in data) {
      setEntityPassword(drawing.id, data.password);
    }

    Object.assign(drawing, data);

    if ('permissions' in data) {
      // in case voice chat permission changed
      this.model.voiceChat.applySettings();
      this.model.voiceChat.updateUserMutes();
    }

    if ('featureFlags' in data) {
      this.model.manage.updateDrawing(drawing);
    }

    // switch socket for voice chat communication if main drawing changed
    const mainDrawingId = getMainDrawingId(drawing);
    if (this.model.voiceChat.drawingId !== mainDrawingId) {
      this.model.voiceChat.drawingId = mainDrawingId;
      if (this.model.voiceChat.isConnected || this.model.voiceChat.isJoining || this.model.voiceChat.isConnecting) {
        console.log('reconnecting voicechat due to main drawing change', mainDrawingId);
        this.model.voiceChat.stop();
        this.model.voiceChat.start().catch(e => DEVELOPMENT && console.error(e));
      }
    }

    redrawDrawing(editor);
    setDocumentTitle(drawing.name);
  }
  // Layers
  @Wait() @ToHistory() @Apply() @Method({ binary: [BinConnId, BinLocalId, BinLayerId] })
  selectLayer(connId: number, localId: number, layerId: number) {
    if (!this.activeConn(connId, 'selectLayer')) return;
    if (invalidModel(this.model)) return;

    const editor = getEditor(this.model);
    const user = this.model.getUserOrTemp(localId);

    if (user && user !== this.model.user) {
      setActiveLayer(user, getLayer(editor.drawing, layerId));
    }
  }
  @Wait() @ToHistory() @Apply() @Method({ binary: [BinConnId, BinLocalId, BinLayerId] })
  ownLayer(connId: number, localId: number, layerId: number) {
    if (!this.activeConn(connId, 'ownLayer')) return;
    if (invalidModel(this.model)) return;

    logAction(`[remote] ownLayer (clientId: ${localId}, layerId: ${layerId})`);
    this.model.pendingLayerOwns.delete(layerId);
    const user = this.model.getUserOrTemp(localId);

    if (user) {
      const editor = getEditor(this.model);
      setOwnedLayer(editor.drawing, layerId, user);

      if (user === this.model.user && (!user.activeLayer || this.model.selectLayerOnOwn === layerId)) {
        selectLayer(editor, getLayer(editor.drawing, layerId));
        this.model.selectLayerOnOwn = 0;
      }
    }
  }
  @Wait() @ToHistory() @Apply() @Method({ binary: [BinConnId, BinLocalId, BinLayerId, Bin.U8] })
  disownLayer(connId: number, localId: number, layerId: number, flags: DisownFlags) {
    if (!this.activeConn(connId, 'disownLayer')) return;
    if (invalidModel(this.model)) return;

    logAction(`[remote] disownLayer (clientId: ${localId}, layerId: ${layerId})`);
    this.model.pendingLayerOwns.delete(layerId);
    const user = this.model.getUserOrTemp(localId);
    const editor = getEditor(this.model);
    const layer = getLayer(editor.drawing, layerId);

    if (layer) {
      if (user && user.activeLayer === layer) setActiveLayer(user, undefined); // has to be before finishTool
      if (user && user.surface.layer === layer) finishTransform(editor, user, 'ClientActions:disownLayer');
      setLayerOwner(layer, undefined, true);
      if (hasFlag(flags, DisownFlags.RemoveOwner)) layer.layerOwner = undefined;
    } else {
      if (DEVELOPMENT && !TESTS) console.warn(`disowning missing layer: ${layerId}`);
      if (user) removeItem(user.ownedLayers, layerId);
    }

    logAction(`[remote] disownLayer > history.clearLayer (clientId: ${localId}, layerId: ${layerId}) ${user ? undosLog(user.history) : '-'}`);
    user?.history.clearLayer(layerId);
  }
  @Wait() @ToHistory() @Apply() @Method({ binary: [BinConnId, BinLocalId, [BinLayerId]] })
  reorderLayers(connId: number, localId: number, order: number[]) {
    if (!this.activeConn(connId, 'reorderLayers')) return;
    if (invalidModel(this.model)) return;

    const editor = getEditor(this.model);
    reorder(editor.drawing.layers, order);
    redrawDrawing(editor);
    logAction(`[remote] reorderLayers (clientId: ${localId}) [${editor.drawing.layers.map(l => l.id).join(', ')}]`);
  }
  // Tools
  @Wait() @Method({ binary: [BinConnId, Bin.U32] })
  confirm(connId: number, index: number) {
    if (!this.activeConn(connId, 'confirm')) return;
    this.model.confirmedTools = index;
  }
  @Wait() @ToHistory() @Method({ binary: [BinConnId, BinLocalId, BinLayerId, Bin.Obj, Bin.U8Array] })
  tool(connId: number, localId: number, layerId: number, tool: IToolData, data: Uint8Array | undefined) {
    if (!this.activeConn(connId, 'tool')) return;
    if (invalidModel(this.model)) return;

    logAction(`[remote] tool: ${getToolFullName(tool)} (clientId: ${localId}, layerId: ${layerId}, t: ${tool.t}${tool.replace ? ', replace' : ''})`);
    void execTool(this.model, localId, layerId, tool, undefined, data);
    this.model.toolPromise?.catch(e => {
      this.model.showError('Failed to draw tool', false, true);
      this.model.reportError('Failed on toolPromise (tool)', e);
    });
  }
  @Wait() @ToHistory() @Method({ binary: [BinConnId, BinLocalId, BinLayerId, Bin.Obj] })
  beginTool(connId: number, localId: number, layerId: number, data: IToolData): void {
    if (!this.activeConn(connId, 'beginTool')) return;
    if (invalidModel(this.model)) return;

    const tool = beginClientTool(this.model, localId, layerId, data);

    if (localId === this.model.user.localId) {
      const editor = getEditor(this.model);
      editor.activeTool = tool;
      editor.activeTool.begin?.(data, false);
    } else {
      const user = this.model.getUserOrTemp(localId);
      if (!user) throw new Error(`Missing user (updateTool, clientId: ${localId})`);
      user.activeTool = tool;
      user.activeTool.begin?.(data, true);
    }
  }
  @Wait() @ToHistory() @Method({ binary: [BinConnId, BinLocalId, Bin.Obj, Bin.Obj] })
  updateTool(connId: number, localId: number, data: IToolData, update: any) {
    if (!this.activeConn(connId, 'updateTool')) return;
    if (invalidModel(this.model)) return;

    const tool = this.getActiveTool(localId, this.model, data, 'updateTool');
    this.model.toolPromise = tool?.update?.(data, update, localId !== this.model.user.localId);
    this.model.toolPromise?.then(async () => {
      this.model.toolPromise = undefined;
      if (!this.model.insideCommitLoop) await commitWaitingActions(this.model);
    }).catch(e => {
      this.model.showError('Failed to draw tool', false, true);
      this.model.reportError('Failed on toolPromise (tool)', e);
    });

    if ('jobId' in data) {
      this.lastJobId = (data as any).jobId;
    }
  }
  @Wait() @ToHistory() @Method({ binary: [BinConnId, BinLocalId, Bin.Obj] })
  finishTool(connId: number, localId: number, data: IToolData): void {
    if (!this.activeConn(connId, 'finishTool')) return;
    if (invalidModel(this.model)) return;

    const tool = this.getActiveTool(localId, this.model, data, 'finishTool');
    if (localId !== this.model.user.localId) {
      const user = this.model.getUserOrTemp(localId);
      if (!user) throw new Error(`Missing user (finishTool, clientId: ${localId})`);
      user.activeTool = undefined;
    }
    tool?.finish?.(true);
  }
  @Wait() @ToHistory() @Method({
    binary: [BinConnId, BinLocalId, BinLayerId, Bin.F64, Bin.F64, Bin.F64, Bin.F64, Bin.Bool, Bin.Obj, BinCoord, BinCoord, BinPressure]
  })
  startToolView(
    connId: number, localId: number, layerId: number, viewX: number, viewY: number, viewScale: number, viewRotation: number,
    viewFlipped: boolean, tool: IToolData, x: number, y: number, p: number
  ) {
    if (!this.activeConn(connId, 'startToolView')) return;
    if (invalidModel(this.model)) return;

    x = decodeCoord(x);
    y = decodeCoord(y);

    this.innerStartToolView(localId, layerId, viewX, viewY, viewScale, viewRotation, viewFlipped, tool, x, y, p, true);
  }
  // internal function so it doesn't trigger ToHistory
  private innerStartToolView(
    localId: number, layerId: number, viewX: number, viewY: number, viewScale: number, viewRotation: number,
    viewFlipped: boolean, tool: IToolData, x: number, y: number, p: number, final: boolean
  ) {
    const model = this.model;
    const user = model.getUserOrTemp(localId);
    const editor = getEditor(model);
    const layer = getLayer(editor.drawing, layerId);

    if (!user) throw new Error(`Missing user (startToolView, clientId: ${localId}, t: ${tool.t})`);

    try {
      logAction(`[remote] start tool: ${toolIdToString(tool.id)} (clientId: ${localId}, layerId: ${layerId}, t: ${tool.t}) (${rectToString(layer?.rect)}) [${editor.drawing.layers.map(l => l.id).join(', ')}]`);

      // TEMP: error tracking
      if (!layer && (tool.id === ToolId.Brush || tool.id === ToolId.Pencil || tool.id === ToolId.Eraser || tool.id === ToolId.LassoBrush)) {
        user.activeTool = undefined; // prevent errors on nextTool and endTool
        throw new Error(`Missing layer (clientActions.innerStartToolView, clientId: ${localId}, layerId: ${layerId}, t: ${tool.t})`);
      }

      if (layer) layer.owner = user;

      setActiveLayer(user, layer);
      verifySelection(model, user, tool);

      const activeTool = setupActiveTool(editor, user, tool);
      let view: Viewport | undefined = undefined;

      if (activeTool.view) {
        view = createViewport(viewX, viewY, viewScale, viewRotation, viewFlipped);
        view.contentWidth = editor.drawing.width;
        view.contentHeight = editor.drawing.height;
        copyViewport(activeTool.view, view);
      }

      activeTool.setup?.(tool);

      if (!activeTool.start) throw new Error('Missing activeTool start (startToolView)');

      activeTool.start(x, y, decodePressure(p));

      if (!user.decoder) throw new Error('Missing user decoder (startToolView)');

      user.lastToolStartData = tool;
      user.lastX = x;
      user.lastY = y;
      startDecoder(user.decoder, x, y, p);

      if (final) lastXYToCursorXY(user, view);
    } catch (e) {
      logAction(`[remote] start tool FAILED (clientId: ${localId}, t: ${tool.t}): ${e.message || e.toString()}`);
      user.activeTool = undefined;
      model.showError(`Error during drawing: ${e.message}`, false, true);
      throw e;
    }
  }
  @Wait() @ToHistory() @Method({
    binary: [BinConnId, BinLocalId, BinLayerId,
      Bin.F64, Bin.F64, Bin.F64, Bin.F64, Bin.Bool,
      Bin.Obj, Bin.Bool, Bin.U8Array, Bin.I16, Bin.I16, Bin.I16, Bin.I16]
  })
  startToolViewWithMoves(
    connId: number, localId: number, layerId: number,
    viewX: number, viewY: number, viewScale: number, viewRotation: number, viewFlipped: boolean,
    tool: IToolData, end: boolean, data: Uint8Array, ax: number, ay: number, aw: number, ah: number
  ) {
    if (!this.activeConn(connId, 'startToolViewWithMoves')) return;
    if (invalidModel(this.model)) return;

    // TEMP: checking for weird error (firefox on mac) (OCT 2020)
    //       movesBuffer was `undefined` (YaBrowser) (APR 2021)
    if (!data || !('byteLength' in data)) {
      let snap = `value: ${data}`;

      if (data) {
        snap += `, keys: ${Object.keys(data).join(', ')}`;
        snap += `, ctor: ${(data as any).constructor}`;
        snap += `, typeof: ${typeof data}`;
      }

      throw new Error(`Invalid movesBuffer (${snap})`);
    }

    const user = this.model.getUserOrTemp(localId)!;
    if (!user.decoder) throw new Error('Missing user decoder (startToolViewWithMoves)');

    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    const endOffset = end ? data.byteLength - 10 : data.byteLength;

    {
      const { x, y, p } = readMovesEntry(view, 0);
      this.innerStartToolView(localId, layerId, viewX, viewY, viewScale, viewRotation, viewFlipped, tool, x, y, p, data.byteLength === 10);
    }

    nextToolArrayDecoder(user.decoder, data.subarray(10, endOffset));

    if (end) {
      const { x, y, p } = readMovesEntry(view, endOffset);
      this.innerEndTool(localId, x, y, p, ax, ay, aw, ah);
    }

    if (!end) {
      const view = createViewport(viewX, viewY, viewScale, viewRotation, viewFlipped);
      view.contentWidth = this.model.editor!.drawing.width;
      view.contentHeight = this.model.editor!.drawing.height;
      lastXYToCursorXY(user, view);
    }
  }
  @Wait() @ToHistory() @Method({ binary: [BinConnId, BinLocalId, Bin.U8Array], ignore: true })
  nextToolArray(connId: number, localId: number, moves: Uint8Array) {
    if (!this.activeConn(connId, 'nextToolArray')) return;
    if (invalidModel(this.model)) return;

    const user = this.model.getUserOrTemp(localId);

    if (!user) return this.logAndReportError(`[remote] nextTool: missing user (clientId: ${localId})`);
    if (!user.activeTool) return this.logAndReportError(`[remote] nextTool: missing activeTool (clientId: ${localId})`);
    if (!user.decoder) throw new Error('Missing user decoder (nextTool)');

    nextToolArrayDecoder(user.decoder, moves);

    if (SHOW_CURSORS && this.model.editor) {
      lastXYToCursorXY(user, user.activeTool.view);
      redraw(this.model.editor);
    }
  }
  @Wait() @ToHistory() @Method({ binary: [BinConnId, BinLocalId, BinCoord, BinCoord, BinPressure, Bin.I16, Bin.I16, Bin.I16, Bin.I16] })
  endTool(connId: number, localId: number, x: number, y: number, p: number, ax: number, ay: number, aw: number, ah: number) {
    if (!this.activeConn(connId, 'endTool')) return;
    if (invalidModel(this.model)) return;

    this.innerEndTool(localId, decodeCoord(x), decodeCoord(y), p, ax, ay, aw, ah);
  }
  // internal function so it doesn't trigger ToHistory
  private innerEndTool(localId: number, x: number, y: number, p: number, ax: number, ay: number, aw: number, ah: number) {
    logAction(`[remote] endTool (clientId: ${localId})`);
    const user = this.model.getUserOrTemp(localId);

    if (!user) return this.logAndReportError(`[remote] endTool: missing user (clientId: ${localId})`);
    if (!user.activeTool) return this.logAndReportError(`[remote] endTool: missing activeTool (clientId: ${localId})`);
    if (!user.activeTool.end) throw new Error('Missing activeTool end (endTool)');

    const selectionTool = isSelectionTool(user.activeTool.id);

    user.activeTool.end(x, y, decodePressure(p));
    user.lastTool = user.activeTool;
    user.activeTool = undefined;

    if (SHOW_CURSORS && this.model.editor) {
      user.lastX = x;
      user.lastY = y;
      lastXYToCursorXY(user, user.lastTool.view);
      redraw(this.model.editor);
    }

    if (user.activeLayer && !selectionTool) {
      verifyAfterRect(this.model, user, user.activeLayer, this.model.editor!.drawing, createRect(ax, ay, aw, ah), 'endTool', user.lastToolStartData);
    }
  }
  @Wait() @ToHistory() @Method({ binary: [BinConnId, BinLocalId] })
  cancelTool(connId: number, localId: number) {
    if (!this.activeConn(connId, 'cancelTool')) return;
    if (invalidModel(this.model)) return;

    logAction(`[remote] cancelTool (clientId: ${localId})`);
    const user = this.model.getUserOrTemp(localId);
    if (!user) return this.logAndReportError(`[remote] cancelTool: missing user (clientId: ${localId})`);
    if (!user.activeTool) return this.logAndReportError(`[remote] cancelTool: missing activeTool (clientId: ${localId})`);

    const editor = getEditor(this.model);
    if (user.activeTool.cancelBeganTool) {
      user.activeTool.cancelBeganTool(true);
    } else {
      cancelToolInternal(editor, user.activeTool, user, false);
    }
    redrawDrawing(editor);
    user.activeTool = undefined;
  }
  @Wait() @ToHistory() @Method({ binary: [BinConnId, BinLocalId, Bin.U32] })
  undo(connId: number, localId: number, t: number) {
    if (!this.activeConn(connId, 'undo')) return;
    if (invalidModel(this.model)) return;

    const user = this.model.getUserOrTemp(localId);
    if (!user) throw new Error(`Missing user (undo, clientId: ${localId})`);

    logAction(`[remote] undo (clientId: ${localId}, t: ${t}) ${undosLog(user.history)}`);
    if (!user.history.canUndo()) {
      this.model.reportError(`invalid undo (clientId: ${localId}, t: ${t})`);
    }
    user.history.undo();
  }
  @Wait() @ToHistory() @Method({ binary: [BinConnId, BinLocalId, Bin.U32] })
  redo(connId: number, localId: number, t: number) {
    if (!this.activeConn(connId, 'redo')) return;
    if (invalidModel(this.model)) return;

    const user = this.model.getUserOrTemp(localId);
    if (!user) throw new Error(`Missing user (redo, clientId: ${localId})`);

    logAction(`[remote] redo (clientId: ${localId}, t: ${t}) ${redosLog(user.history)}`);
    if (!user.history.canRedo()) {
      this.model.reportError(`invalid redo (clientId: ${localId}, t: ${t})`);
    }
    user.history.redo();
  }
  @Wait() @Apply() @Method({ binary: [BinConnId, Bin.U8, Bin.Obj] })
  clientAction(connId: number, action: ClientAction, param?: any) {
    if (!this.isActive && action !== ClientAction.SwitchSocket) return;

    switch (action) {
      case ClientAction.LeaveLayer: {
        if (!this.activeConn(connId, 'LeaveLayer')) break;

        const layerId = param | 0;

        // TODO: refactor all this cancelling into some better place
        const editor = this.model.editor!;
        let filtering = !!editor.filter?.activeFilter;

        logAction(`[remote] leaveLayer (filtering: ${filtering}, layerId: ${layerId})`);

        if (filtering) {
          if (editor.activeLayer?.id === layerId) {
            // cancel filter if it's on layer that we want to free
            editor.filterCancelled.next();
            filtering = false;
          } else {
            // disable `drawingInProgress` to allow for `disownLayer`
            editor.drawingInProgress = false;
          }
        }

        if (editor.hasActiveTool() && editor.activeToolLayerIds().includes(layerId)) {
          editor.activeTool?.cancelBeganTool?.();
        }

        nowOrAfterFinish(editor, editor => {
          logAction(`[remote] trigger disown (layerId: ${layerId})`);
          disownLayer(editor, getLayer(editor.drawing, layerId)).catch(e => DEVELOPMENT && console.error(e));
        });

        if (filtering) editor.drawingInProgress = true;
        break;
      }
      case ClientAction.LeaveAllLayers: {
        if (!this.activeConn(connId, 'LeaveAllLayers')) break;

        // cancel filter
        if (this.model.editor?.filter?.activeFilter) {
          this.model.editor.filterCancelled.next();
        }

        nowOrAfterFinish(this.model.editor, editor => {
          logAction(`[remote] trigger disown all`);
          disownAllLayers(editor).catch(e => DEVELOPMENT && console.error(e));
        });
        break;
      }
      case ClientAction.Sleep:
        logAction(`[remote] trigger sleep`);
        this.model.isSleeping = true;
        this.model.connectionService.disconnect();
        break;
      case ClientAction.LoadErrorMessage: {
        if (!this.activeConn(connId, 'LoadErrorMessage')) break;

        logAction(`[remote] load error message (${param})`);
        DEVELOPMENT && console.warn('Loading error', param);
        this.model.error = undefined;
        this.model.passwordNeeded = false;
        this.model.loaded = 1;
        this.model.failed = true;

        if (IS_HOSTED && param === SIGN_IN_TO_VIEW_ERROR) {
          return; // we're showing this message in alerts
        }

        if (IS_PORTAL && failedErrors[param]) {
          this.model.failedDrawingLoad = failedErrors[param];
          return;
        }

        if (IS_PORTAL && param === DRAWING_DELETED) {
          this.model.modals.drawingDeleted();
          return;
        }

        if (IS_PORTAL && isUserLimitError(param)) {
          this.model.modals.userLimit(+param.replace(/[^\d]/g, ''));
        }

        if (IS_PORTAL && param === CLIENT_LIMIT_ERROR && !shownOverloaded) {
          this.model.modals.overloaded();
          shownOverloaded = true;
        }

        if (param === PASSWORD_ERROR) {
          this.model.passwordNeeded = true;
        } else {
          this.model.showError(param);
        }

        if (DEVELOPMENT && /no active workers/.test(param)) {
          setTimeout(() => this.model.reconnect(), 500);
        }
        break;
      }
      case ClientAction.SwitchSocket: {
        if (this.isActive) logAction(`[remote] switch sockets`);
        DEVELOPMENT && console.log('switch sockets', param);

        if (param) {
          for (const id of param as string[]) {
            removeItem(this.socket.drawings, id);
          }

          this.model.connectionService.update();
          return;
        }

        this.model.reconnect();
        break;
      }
      case ClientAction.UpdateOwnerName: {
        if (!this.activeConn(connId, 'UpdateOwnerName')) break;

        if (this.model.editor) {
          const { from, to, color } = param as UpdateOwnerName;
          // update layer owner names
          for (const layer of this.model.editor.drawing.layers) {
            if (layer.layerOwner?.name === from) {
              layer.layerOwner.name = to;
              layer.layerOwner.color = color ?? layer.layerOwner.color;
            }
          }
        }
        break;
      }
      case ClientAction.AccountCreated: {
        sendGAEvent('Sign up', 'Signed up', param);
        break;
      }
      case ClientAction.DebugLayers: {
        const model = this.model;
        model.server.debug('layers', `\n[${getTimestamp(Date.now())}] clientId: ${model.user.localId}\n` +
          `users: [${model.users.map(u => u.localId).join(', ')}]\n` +
          `layers: [${model.editor?.drawing.layers.map(l => l.id).join(', ')}]\n` +
          `actions:\n${getActionLog().map(x => `  ${x}`).join('\n')}\n\n`);
        break;
      }
      case ClientAction.DebugLayerSnapshot: {
        if (this.model.editor) {
          sendLayerSnapshot(this.model.editor, param | 0).catch(e => DEVELOPMENT && console.error(e));
        }
        break;
      }
      case ClientAction.DeletingDrawing: {
        const drawing = this.model.editor?.drawing;
        if (drawing?.id && drawing.sequence.length > 1) {
          const newDrawing = drawing.sequence.find(x => x.id !== drawing.id);
          if (newDrawing) navigateToDrawing(this.model.manage.router, newDrawing.id, 'deleting-drawing');
        }
        break;
      }
      case ClientAction.AiPromptAdded: {
        const drawing = this.model.editor?.drawing;
        drawing?.promptHistory?.unshift(param);
        break;
      }
      case ClientAction.AiPromptsList: {
        const aiTool = this.model.editor?.aiTool;
        const items = param as PromptHistoryItem[];
        aiTool?.appendPromptHistory(items);
        break;
      }
      case ClientAction.AiUsageUpdated: {
        const aiTool = this.model.editor?.aiTool;
        if (aiTool) aiTool.usageQuota = param as { used: number, total: number };
        break;
      }
      default: invalidEnum(action);
    }
  }
  @Apply() @Method({ binary: [Bin.U8, Bin.Obj] })
  voiceChatAction(action: VoiceChatAction, param: unknown) {
    const model = this.model;
    const voiceChat = model.voiceChat;

    switch (action) {
      case VoiceChatAction.Session:
      case VoiceChatAction.SessionInitial: {
        const hadSession = !!voiceChat.session;
        voiceChat.session = param as boolean;
        if (!voiceChat.session) voiceChat.setToken(undefined);

        // ignore if we're getting session while opening the drawing
        if (voiceChat.session && !hadSession && action !== VoiceChatAction.SessionInitial) {
          voiceChat.playSound('started');
        }

        // reconnect if user closed/refreshed tab with ongoing voice chat call
        const connected = storageGetJson('voice-chat-connected') as { id: string; time: number; } | undefined;
        const mainDrawingId = getMainDrawingId(model.editor?.drawing);
        storageRemoveItem('voice-chat-connected');
        if (connected && connected.id === mainDrawingId && (Date.now() - connected.time) < (5 * MINUTE)) {
          voiceChat.join().catch(e => DEVELOPMENT && console.error(e));
        }
        break;
      }
      case VoiceChatAction.Token: {
        voiceChat.session = true;
        voiceChat.drawingId = getMainDrawingId(model.editor?.drawing);
        voiceChat.setToken(param as string);
        break;
      }
      case VoiceChatAction.Joined: {
        if (param !== model.user.uniqId) {
          voiceChat.joined.add(param as string);
        }
        break;
      }
      case VoiceChatAction.Left: {
        if (param === model.user.uniqId) {
          // force user to leave by themselves
          voiceChat.stop();
        } else {
          voiceChat.joined.delete(param as string);
        }
        break;
      }
      case VoiceChatAction.Mute: {
        voiceChat.muted.add(param as string);
        break;
      }
      case VoiceChatAction.Unmute: {
        voiceChat.muted.delete(param as string);
        break;
      }
      case VoiceChatAction.MuteAdmin: {
        voiceChat.mutedByAdmin.add(param as string);
        voiceChat.updateUserMutes();
        break;
      }
      case VoiceChatAction.UnmuteAdmin: {
        voiceChat.mutedByAdmin.delete(param as string);
        voiceChat.updateUserMutes();
        break;
      }
      case VoiceChatAction.ListOfActions: {
        const actions = param as [VoiceChatAction, string | boolean][];
        for (const [a, p] of actions) {
          this.voiceChatAction(a, p);
        }
        break;
      }
      default: invalidEnum(action);
    }
  }
  @Method({ binary: [BinConnId, [BinLocalId, Bin.F32, Bin.F32]] })
  cursors(connId: number, cursors: [number, number, number][]) {
    if (!this.activeConn(connId, 'cursors')) return;

    for (const [localId, x, y] of cursors) {
      const user = this.model.getUserByLocalId(localId);

      if (DEVELOPMENT && !user) console.warn('Missing user for cursor', localId);

      if (user && user !== this.model.user && (user.cursorX !== x || user.cursorY !== y)) {
        user.cursorX = x;
        user.cursorY = y;
        user.cursorLastUpdate = performance.now();
      }
    }

    this.model.editor && redraw(this.model.editor); // TODO: only redraw relevant parts ?
  }
  private getSequenceDrawing(drawingId: string) {
    const editor = getEditor(this.model);
    const drawing = findById(editor.drawing.sequence, drawingId);

    if (!drawing) {
      DEVELOPMENT && console.warn(`Missing drawing in sequence (${drawingId})`);
      return undefined;
    }

    return drawing;
  }
  @Apply() @Method({ binary: [Bin.Str] })
  updateSequenceThumb(drawingId: string) {
    if (invalidModel(this.model)) return;

    const drawing = this.getSequenceDrawing(drawingId);

    if (drawing) {
      // ignore if we have recent thumb data
      if (drawing.thumbData && drawing.thumbUpdated && drawing.thumbUpdated > (Date.now() - MINUTE)) return;

      drawing.thumbTimestamp = Date.now();
    }
  }
  @Apply() @Method({ binary: [Bin.Str, Bin.U16, Bin.U16, Bin.U8, Bin.U8, Bin.U8Array] })
  updateSequenceThumbData(drawingId: string, x: number, y: number, thumbWidth: number, thumbHeight: number, compressed: Uint8Array) {
    if (invalidModel(this.model)) return;

    const drawing = this.getSequenceDrawing(drawingId);

    if (drawing) {
      if (!drawing.thumbData) {
        drawing.thumbData = getPixelContext().createImageData(thumbWidth, thumbHeight);
        drawing.thumbImage = undefined;
      }

      const { width, height, data } = decompressImageDataRLE(compressed, (width, height, data) =>
        ({ width, height, data: data ?? new Uint8ClampedArray(width * height * 4), colorSpace: 'srgb' }), true);

      replaceImageDataRect(drawing.thumbData, data, { x, y, w: width, h: height });
      redrawSequenceThumbCanvas(drawing); // TODO: or move this to update()
    }
  }
  @Apply() @Method({ binary: [Bin.Str, Bin.Obj] })
  updateSequenceDrawing(drawingId: string, update: { name?: string }) {
    if (invalidModel(this.model)) return;

    const drawing = this.getSequenceDrawing(drawingId);
    if (drawing) Object.assign(drawing, update);
  }
  @Apply() @Method({ binary: [Bin.Str, BinSequenceUser] })
  updateSequenceUsers(drawingId: string, users: SequenceUserData[]) {
    if (invalidModel(this.model)) return;

    const drawing = this.getSequenceDrawing(drawingId);
    if (!drawing) return;

    for (const [uniqId, name, color, avatar, role, anonymous, anonymousNumber] of users) {
      const state: UserData = { uniqId, name, color, avatar, role, anonymous, anonymousNumber, localId: 0, ownedLayers: [] };
      const existing = drawing.users.find(u => u.uniqId === uniqId);

      if (existing) {
        setUserState(existing, state);
      } else {
        const user = createUser(uniqId, 0);
        setUserState(user, state);
        drawing.users.push(user);
      }
    }
  }
  @Apply() @Method({ binary: [Bin.Str, [Bin.Str]] })
  removeSequenceUsers(drawingId: string, uniqIds: string[]) {
    if (!this.model.editor) return;

    const drawing = this.getSequenceDrawing(drawingId);
    if (!drawing) return;

    for (let i = drawing.users.length - 1; i >= 0; i--) {
      if (uniqIds.includes(drawing.users[i].uniqId)) {
        drawing.users.splice(i, 1);
      }
    }
  }
  @Apply() @Method({ binary: [Bin.U8, Bin.Obj] })
  sequenceAction(action: SequenceAction, param: unknown) {
    if (invalidModel(this.model)) return;

    switch (action) {
      case SequenceAction.PresentDrawing: {
        navigateToDrawing(this.model.manage.router, param as string, 'presentation');
        break;
      }
      case SequenceAction.PresentViewport: {
        if (this.model.editor && !this.model.isPresentationHost) {
          const view = param as Viewport;
          nowOrAfterFinish(this.model.editor, editor => {
            editor.lastPresentationViewToMatch = view;
            matchFromOtherViewport(editor.view, view);
          });
        }
        break;
      }
      case SequenceAction.PresentationMode: {
        const presentationMode = param as PresentationModeState;
        const editor = this.model.editor;
        dismissChatHelp();
        this.model.setPresentationMode(presentationMode);

        if (this.model.isPresentationMode) {
          this.model.modals.closeAllMarketingModals();
          if (editor && !this.model.presentationModeState.followingHostViewportEnforced && !this.model.isPresentationHost) {
            fitViewportOnScreen(editor.view);
          }
        }
        if (!this.model.activePresentation) {
          this.model.modals.closeAllPresentationModeModals();
        }
        break;
      }
      case SequenceAction.PresentationModeInvite: {
        const { isForced, invitedUsers, name } = param as PresentationModeInvite;
        this.model.modals.presentationActionModal({
          title: 'You are invited to join Presentation',
          user: name,
          mode: PresentationActions.InvitedToPresentation,
          timer: isForced ? PRESENTATION_VIEWER_FORCED_INVITE_TIME : undefined,
          forced: isForced,
          state: this.model.presentationModeState,
          viewers: invitedUsers
        }).then(respone => respone && this.model.joinPresentation()).catch(e => DEVELOPMENT && console.error(e));
        break;
      }
      case SequenceAction.EndedPresentationMode: {
        this.model.modals.presentationActionModal({
          title: 'Presentation is over',
          mode: PresentationActions.presentationIsOver,
          state: this.model.presentationModeState,
        }).then(() => this.model.goBackToPreviousViewPresnetationMode()).catch(e => DEVELOPMENT && console.error(e));
        break;
      }
      case SequenceAction.PresentationModeKickedOut: {
        this.model.leavePresentation();
        break;
      }
      case SequenceAction.JoinPresentation: {
        const { viewer } = param as JoinPresentation;
        const viewersUniqIds = this.model.presentationModeState.viewersUniqIds || [];
        const coPresentersUniqIds = this.model.presentationModeState.coPresentersUniqIds || [];
        const user = this.model.getUserByUniqId(viewer, true);
        if (user && this.model.editor && hasPermission(this.model.editor.drawing, user, 'takeOverPresentation')) {
          coPresentersUniqIds.push(viewer);
        }
        viewersUniqIds.push(viewer);
        this.model.presentationModeState.viewersUniqIds = viewersUniqIds;
        this.model.presentationModeState.coPresentersUniqIds = coPresentersUniqIds;
        if (this.model.isPresentationHost) this.model.updatePresentationMode({ viewersUniqIds, coPresentersUniqIds });
        break;
      }
      case SequenceAction.LeavePresentation: {
        const { viewer } = param as LeavePresentation;
        const viewersUniqIds = (this.model.presentationModeState.viewersUniqIds || []).filter(u => u !== viewer);
        if (viewer) {
          this.model.presentationModeState.viewersUniqIds = viewersUniqIds;
          if (this.model.isPresentationHost) this.model.updatePresentationMode({ viewersUniqIds });
        }
        break;
      }
      case SequenceAction.ClosePresentationActionModals: {
        this.model.modals.closeAllPresentationModeModals();
        break;
      }
      case SequenceAction.PassPresenterRole: {
        this.model.modals.presentationActionModal({
          title: 'You have been given the presentation.',
          mode: PresentationActions.givenPresenterRole
        }).then(response => response && this.model.passOverPresentation()
        ).catch(e => DEVELOPMENT && console.error(e));
        break;
      }
      case SequenceAction.RemovePresentationHostStatus: {
        storageRemoveItem('isPresentationModeHost');
        break;
      }
      case SequenceAction.UserDisconnectedPresentationMode: {
        const uniqId = param as string;
        const viewersClientIds = this.model.presentationModeState.viewersUniqIds || [];
        const { host } = this.model.presentationModeState;
        this.model.presentationModeState.viewersUniqIds = viewersClientIds.filter(u => u !== uniqId);

        if (host?.uniqIds?.includes(uniqId) && this.model.isPresentationViewer) {
          if (host.uniqIds.length <= 1) {
            this.model.updatePresentationMode({ host });
            if (this.model.isPresentationCoPresenter(this.model.user.uniqId)) {
              this.model.modals.presentationActionModal({
                title: 'Take over Presentation',
                mode: PresentationActions.takeOverPresentation,
                timer: PRESENTATION_TAKE_OVER_TIME
              }).then(response => {
                if (response) {
                  this.model.takeOverPresentation();
                } else if (response === false) {
                  this.model.stopPresentationMode();
                }
              }).catch(e => DEVELOPMENT && console.error(e));
            } else {
              this.model.modals.presentationActionModal({
                title: 'Host is disconnected',
                mode: PresentationActions.presentationHostDisconnected,
                user: host.name,
                timer: PRESENTATION_HOST_DISCONNECTED_TIME
              }).then(response => {
                if (response) {
                  this.model.leavePresentation();
                  if (this.model.presentationModeState.viewersUniqIds?.length === 1 || this.model.presentationModeState.host!.uniqIds.length <= 1) this.model.stopPresentationMode();
                }
              }).catch(e => DEVELOPMENT && console.error(e));
            }
          }
        } else if (this.model.isPresentationHost && host?.uniqIds.includes(uniqId)) {
          this.model.updatePresentationMode({ host: { ...host, uniqIds: host.uniqIds.filter(id => uniqId !== id)}});
        } else if (host?.uniqIds?.includes(uniqId) && this.model.presentationModeState.host!.uniqIds.length <= 1) {
          this.model.stopPresentationMode();
        }
        break;
      }
      default: invalidEnum(action);
    }
  }
  @Wait() @ToHistory() @Method({ binary: [BinLocalId, Bin.U32, Bin.U32, Bin.U32, Bin.U8Array] })
  clientDataChunk(localId: number, id: number, size: number, offset: number, chunk: Uint8Array) {
    if (!this.isActive) return;

    // TODO: need to handle chunks when opening a drawing before using this method
    const user = this.model.getUserByLocalId(localId);

    if (!user) {
      DEVELOPMENT && console.warn(`[clientDataChunk] User does not exist (localId: ${localId})`);
      return;
    }

    const chunkedData = user.chunkedData.get(id);
    if (chunkedData) {
      chunkedData.set(chunk, offset);
    } else {
      const c = new Uint8Array(size);
      c.set(chunk, offset);
      user.chunkedData.set(id, c);
    }

    // TODO when this will be used somewhere it would be a good idea to mark chunks as complete
  }
  @Wait() @ToHistory() @Method({ binary: [BinLocalId, Bin.U32] })
  cancelChunkedData(localId: number, id: number) {
    if (!this.isActive) return;

    const user = this.model.getUserByLocalId(localId);

    if (!user) {
      DEVELOPMENT && console.warn(`[cancelChunkedData] User does not exist (localId: ${localId})`);
      return;
    }

    user.chunkedData.delete(id);
  }
  // TODO: make specialized method for loading image data instead ?
  private datas = new Map<number, Uint8Array>();
  @Method({ binary: [Bin.U32, Bin.U32, Bin.U32, Bin.U8Array] })
  dataChunk(id: number, size: number, offset: number, chunk: Uint8Array) {
    if (!this.isActive) return;

    let data = this.datas.get(id);

    if (!data) {
      data = new Uint8Array(size);
      this.datas.set(id, data);
    }

    data.set(chunk, offset);
  }
  @Method({})
  shapeShape(id: string, name: string, width: number, height: number, path: string, iconWidth: number, iconHeight: number, iconPath: string | undefined) {
    addShape({
      id,
      name,
      path: createShapePath(width, height, path),
      icon: iconPath ? createShapePath(iconWidth, iconHeight, iconPath) : undefined,
    });
  }
  @Method({ binary: [Bin.Str, Bin.Str, Bin.U32, Bin.U32, Bin.U8Array] })
  brushShape(id: string, name: string, width: number, height: number, compressed: Uint8Array) {
    if (!brushShapesMap.has(id)) {
      addBrushShape({ id, name, imageData: { width, height, compressed } });
    }
  }
  @Method({})
  brushShapePath(id: string, name: string, width: number, height: number, path: string) {
    if (!brushShapesMap.has(id)) {
      addBrushShape({ id, name, path: createShapePath(width, height, path) });
    }
  }
  @Method({ binary: [Bin.Str, Bin.U8Array] })
  fileContent(name: string, content: Uint8Array | undefined) {
    this.model.fileContent(name, content);
  }

  private getActiveTool(localId: number, model: Model, data: IToolData, caller: string) {
    if (localId === model.user.localId) {
      const editor = getEditor(model);
      if (!editor.activeTool) throw new Error(`Missing activeTool (${caller}, clientId: ${localId})`);
      if (editor.activeTool.id !== data.id && editor.activeTool.navigation) {
        // eyedropper, move tool or other navigation tool is currently used. Find began tool and send update to that tool
        const tool = findById(editor.tools, data.id);
        if (!tool) throw new Error(`Missing tool (${caller}, clientId: ${localId}, toolId: ${data.id})`);
        if (!tool.hasBegan) throw new Error(`Selected tool was not begun (${caller}, clientId: ${localId}, toolId: ${data.id})`);
        return tool;
      } else if (editor.activeTool.id === data.id) {
        return editor.activeTool;
      } else {
        if ('jobId' in data && this.lastJobId === (data as any).jobId) {
          DEVELOPMENT && console.warn(`Received update for already finished/canceled tool,  lastJobId: ${this.lastJobId} jobId: ${(data as any).jobId}`);
          return undefined;
        }
        throw new Error(`Received updateTool for different tool (${caller}, clientId: ${localId}), activeTool: ${editor.activeTool.id}, toolId: ${data.id}`);
      }
    } else {
      const user = model.getUserOrTemp(localId);
      if (!user) throw new Error(`Missing user (${caller}, clientId: ${localId})`);
      if (!user.activeTool) throw new Error(`Missing activeTool (${caller}, clientId: ${localId})`);
      if (user.activeTool.id !== data.id) throw new Error(`Invalid activeTool (${caller}, clientId: ${localId})`);

      return user.activeTool;
    }
  }

  private logAndReportError(message: string) {
    logAction(message);
    this.model.reportError(message);
  }
}


function nowOrAfterFinish(editor: Editor | undefined, func: (editor: Editor) => void) {
  if (editor) {
    if (editor.drawingInProgress) {
      logAction('delay action after finish');
      editor.afterFinish.push(() => func(editor));
    } else {
      func(editor);
    }
  }
}

function getEditor(model: Model) {
  if (!model.editor) throw new Error('Editor not initialized');
  return model.editor;
}

function getCallerName(steps: number) {
  const stack = (new Error()).stack?.replace(/^Error\s+/i, '') ?? '';
  return stack.split(/\n/g)[steps + 1]?.trim().replace(/^at /, '').split(/[ @]/)[0] ?? '?';
}

function invalidModel(model: Model) {
  if (!model.editor || !model.editor.drawing.id || model.fatalError || model.loadingFailed) {
    const msg = `invalid model (editor: ${!!model.editor}, drawingId: ${model.editor?.drawing.id}, fatarError: ${model.fatalError}] (${getCallerName(1)})`;
    DEVELOPMENT && !TESTS && console.warn(msg);
    logAction(msg);
    return true;
  } else {
    return false;
  }
}

async function commitWaitingActions(model: Model) {
  model.insideCommitLoop = true;

  while (model.waitingActions.length) {
    model.waitingActions.shift()!();
    if (model.toolPromise) await model.toolPromise;
  }

  model.insideCommitLoop = false;
}

function beginClientTool(model: Model, localId: number, layerId: number, tool: IToolData) {
  const user = model.getUserOrTemp(localId);
  if (!user) throw new Error(`No user (${localId})`);

  const editor = getEditor(model);
  const layer = getLayer(editor.drawing, layerId);

  if (layer) layer.owner = user;

  setActiveLayer(user, layer);

  if (user.activeTool?.id !== tool.id) {
    user.activeTool = createTool(tool.id, editor, createFakeModel(user));
  }

  return user.activeTool;
}

async function execToolWithUpdates(model: Model, localId: number, layerId: number, tool: IToolData, updates: any[]) {
  const t = beginClientTool(model, localId, layerId, tool);
  t.begin?.(tool);
  for (const update of updates) {
    await t.update?.(tool, update);
  }
  t.finish?.();
}

function checkSelectionBefore(id: ToolId) {
  // all tools with start..move..end
  return id === ToolId.Brush || id === ToolId.Pencil || id === ToolId.Eraser || id === ToolId.LassoBrush || id === ToolId.LassoSelection;
}

function execTool(
  model: Model, localId: number, layerId: number, tool: IToolData, viewState: ViewportState | undefined,
  data: Uint8Array | undefined, finishAfter = false
) {
  const user = model.getUserOrTemp(localId);
  if (!user) throw new Error(`No user (${localId})`);

  const editor = getEditor(model);
  let prevActiveLayer = user.activeLayer;

  const checkBefore = checkSelectionBefore(tool.id);
  if (checkBefore) verifySelection(model, user, tool);
  const promise = applyTool(editor, user, layerId, tool, viewState, data);

  if (promise) {
    return model.toolPromise = promise
      .then(async () => {
        execToolEnd(editor, user, prevActiveLayer);
        if (finishAfter) finishTransform(editor, user, 'execTool-1');
        if (!checkBefore) verifySelection(model, user, tool);
        model.toolPromise = undefined;
        if (!model.insideCommitLoop) await commitWaitingActions(model);
      })
      .catch(e => {
        logAction(`Error in toolPromise ${e.message}`);
        throw e;
      });
  } else {
    execToolEnd(editor, user, prevActiveLayer);
    if (finishAfter) finishTransform(editor, user, 'execTool-2');
    if (!checkBefore) verifySelection(model, user, tool);
    return undefined;
  }
}

function execToolEnd(editor: Editor, user: User, prevActiveLayer: Layer | undefined) {
  if (prevActiveLayer && !includes(editor.drawing.layers, prevActiveLayer)) {
    prevActiveLayer = undefined;
  }

  editor.apply(() => setActiveLayer(user, prevActiveLayer));
}

// chat

function createChatMessage(user: User | undefined, type: ChatType, message: string): IChatMessage {
  const msg: IChatMessage = { user, type, messages: [message] };

  if (type === ChatType.Invite) {
    msg.openId = message;
    msg.messages = [`You are invited to join new drawing`];
  }

  return msg;
}

function chatMessage(model: Model, uniqId: string, message: string, type: ChatType) {
  model.onBeforeChatMessage.next();

  const user = model.getUserByUniqId(uniqId, true);
  const lastMessage = model.messages[model.messages.length - 1];

  if (lastMessage && lastMessage.user?.uniqId === user?.uniqId && lastMessage.type === type && type !== ChatType.Invite) {
    lastMessage.messages.push(message);
  } else {
    model.messages.push(createChatMessage(user, type, message));
  }

  while (model.messages.length > CHAT_MESSAGE_HISTORY_LIMIT) {
    model.messages.shift();
  }

  if (user?.uniqId !== model.user.uniqId && !model.chatOpened) {
    const msg = createChatMessage(user, type, message);

    if (model.message || model.messageQueue.length) {
      model.messageQueue.push(msg);
    } else {
      void showMessage(model, msg);
    }
  }
}

async function showMessage(model: Model, message: IChatMessage) {
  model.message = message;
  await delay(5000 + (message.messages[0].length * 50) * (message.type === ChatType.Invite ? 2 : 1));
  model.message = undefined;
  await delay(200);

  if (model.messageQueue.length) {
    void showMessage(model, model.messageQueue.shift()!);
  }
}

// other

function releaseUsers(users: User[], editor: Editor | undefined) {
  for (const user of users) {
    if (user.surface.context) releaseToolRenderingContext(user);
    releaseUser(user, editor);
  }
}

export function clearAllUsers(model: Model) {
  releaseUsers(model.users, model.editor);
  releaseUsers(model.tempUsers, model.editor);
  model.users = [];
  model.tempUsers = [];
}
